/**
 * Resolve the visitor's GA4 client_id and session_id for Measurement Protocol.
 *
 * Order: first-party cookies (Next cookies() or the Cookie header), then the
 * posted hidden fields `ga_client_id` / `ga_session_id` the form filled from
 * gtag('get') or document.cookie. Never invent an id.
 */

import {
  GA_CLIENT_ID_FIELD,
  GA_SESSION_ID_FIELD,
  clientIdFromGaCookie,
  cookieValueFromStoreOrHeader,
  gaStreamCookieName,
  parseGaSessionId,
  parsePostedGaClientId,
  sessionFromCookieHeader,
  sessionFromCookieStore,
  sessionFromGaStreamCookie,
  type Ga4BrowserSession,
} from '@/lib/analytics/ga-cookie-parse'

export {
  GA_CLIENT_ID_FIELD,
  GA_SESSION_ID_FIELD,
  parseGaClientId,
  parseGaSessionId,
  parsePostedGaClientId,
} from '@/lib/analytics/ga-cookie-parse'

export type PostedGaIds = {
  clientId: string | null
  sessionId: string | null
}

export type WithPostedGaIds = {
  ga_client_id?: string | null
  ga_session_id?: string | null
}

export type ResolvedGaBrowserIds = {
  clientId: string | null
  session: Ga4BrowserSession | null
}

function measurementId(): string {
  return (
    process.env.GA4_MEASUREMENT_ID ||
    process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID ||
    ''
  ).trim()
}

export function postedGaIdsFrom(input: unknown): PostedGaIds {
  if (!input || typeof input !== 'object') return { clientId: null, sessionId: null }
  if (typeof FormData !== 'undefined' && input instanceof FormData) {
    return postedGaIdsFromFormData(input)
  }
  const bag = input as Record<string, unknown>
  return {
    clientId: parsePostedGaClientId(bag[GA_CLIENT_ID_FIELD] ?? bag.gaClientId ?? bag.client_id),
    sessionId: parseGaSessionId(bag[GA_SESSION_ID_FIELD] ?? bag.gaSessionId ?? bag.session_id),
  }
}

export function postedGaIdsFromFormData(form: FormData): PostedGaIds {
  return {
    clientId: parsePostedGaClientId(form.get(GA_CLIENT_ID_FIELD)),
    sessionId: parseGaSessionId(form.get(GA_SESSION_ID_FIELD)),
  }
}

export function resolveGaBrowserIds(args: {
  cookieStore?: { get(name: string): { value: string } | undefined; getAll?: () => Array<{ name: string; value: string }> } | null
  cookieHeader?: string | null
  posted?: { clientId?: string | null; sessionId?: string | null } | PostedGaIds | null
  measurementId?: string
}): ResolvedGaBrowserIds {
  const mid = (args.measurementId || measurementId()).trim()
  const store = args.cookieStore
  const header = args.cookieHeader
  const cookieClient = clientIdFromGaCookie(cookieValueFromStoreOrHeader(store, header, '_ga'))
  const namedSession = mid
    ? sessionFromGaStreamCookie(cookieValueFromStoreOrHeader(store, header, gaStreamCookieName(mid)))
    : null
  const cookieSession =
    namedSession ?? sessionFromCookieStore(store) ?? sessionFromCookieHeader(header)
  const postedClient = parsePostedGaClientId(args.posted?.clientId)
  const postedSession = parseGaSessionId(args.posted?.sessionId)
  return {
    clientId: cookieClient ?? postedClient,
    session: cookieSession ?? (postedSession ? { sessionId: postedSession } : null),
  }
}

function readDocumentCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  return cookieValueFromStoreOrHeader(null, document.cookie, name)
}

type GtagGet = (command: 'get', target: string, field: string, callback: (value: unknown) => void) => void

let primed: PostedGaIds = { clientId: null, sessionId: null }

function idsFromDocumentCookies(): PostedGaIds {
  const mid = measurementId()
  const named = mid ? sessionFromGaStreamCookie(readDocumentCookie(gaStreamCookieName(mid))) : null
  const scanned = named ?? sessionFromCookieHeader(typeof document === 'undefined' ? '' : document.cookie)
  return {
    clientId: clientIdFromGaCookie(readDocumentCookie('_ga')) ?? null,
    sessionId: scanned?.sessionId ?? null,
  }
}

function mergeIds(base: PostedGaIds, extra: PostedGaIds): PostedGaIds {
  return {
    clientId: extra.clientId ?? base.clientId,
    sessionId: extra.sessionId ?? base.sessionId,
  }
}

/**
 * Prime the in-memory ids from gtag('get', measurementId, ...). Cookie parse
 * fills first so a submit that races the callback still has a value.
 */
export function primeGaBrowserIds(onChange?: (ids: PostedGaIds) => void): PostedGaIds {
  primed = mergeIds(primed, idsFromDocumentCookies())
  if (typeof window === 'undefined') {
    onChange?.(primed)
    return primed
  }
  const gtag = window.gtag as GtagGet | undefined
  const mid = measurementId()
  if (typeof gtag === 'function') {
    gtag('get', mid, 'client_id', (value) => {
      primed = mergeIds(primed, { clientId: parsePostedGaClientId(value), sessionId: null })
      onChange?.(primed)
    })
    gtag('get', mid, 'session_id', (value) => {
      primed = mergeIds(primed, { clientId: null, sessionId: parseGaSessionId(value) })
      onChange?.(primed)
    })
  }
  onChange?.(primed)
  return primed
}

/** Sync read for a form submit: cookies, then any gtag values primed earlier. */
export function readGaBrowserIds(): PostedGaIds {
  return mergeIds(primed, idsFromDocumentCookies())
}

/** Attach posted ids to a server-action payload. No-op on the server. */
export function withPostedGaIds<T extends object>(input: T): T & WithPostedGaIds {
  if (typeof window === 'undefined') return input
  const ids = readGaBrowserIds()
  return {
    ...input,
    ...(ids.clientId ? { [GA_CLIENT_ID_FIELD]: ids.clientId } : {}),
    ...(ids.sessionId ? { [GA_SESSION_ID_FIELD]: ids.sessionId } : {}),
  }
}

/** Write the hidden fields onto a FormData the visitor is about to post. */
export function appendPostedGaIds(form: FormData): FormData {
  if (typeof window === 'undefined') return form
  const ids = readGaBrowserIds()
  if (ids.clientId) form.set(GA_CLIENT_ID_FIELD, ids.clientId)
  if (ids.sessionId) form.set(GA_SESSION_ID_FIELD, ids.sessionId)
  return form
}
